import {chain} from 'lodash';
import {createSelector} from 'reselect';

import {EntryPointTypesEnum} from '../../../applications/bridge/v2/types/bridgeTypesV2';
import {LegacyEntryPoint} from '../../../helpers/models/legacyApplicationHelpers';
import {AppParametricSelector} from '../../../helpers/reselectHelpers';
import {FrameTypesEnum} from '../../../models/api/apiTypes';
import {AppState} from '../../appState';
import {getUserNamespace} from '../teammates/teammatesSelectors';

/*
 * Applications.
 */

/** Get the map of all applications. */
export function getLegacyApplications(state: AppState) {
  return state.data.legacyApplications;
}

export const getFlatLegacyApplications = createSelector([getLegacyApplications], (applications) =>
  chain(applications).values().compact().value(),
);

/** Get a flat list of all applications enabled for the current user. */
export const getFlatEnabledLegacyApplications = createSelector(
  [getFlatLegacyApplications, getUserNamespace],
  (applications, userNamespace) =>
    applications.filter((application) => {
      if (!application.isEnabled || !application.bundleUrl) {
        return false;
      }

      const userPreferences = application.namespacePreferences[userNamespace];
      const globalPreferences = application.namespacePreferences.global;

      return globalPreferences?.isInstalled || userPreferences?.isInstalled;
    }),
);

export function makeGetLegacyApplicationFromEntryPoint<P>(
  getEntryPoint: AppParametricSelector<P, LegacyEntryPoint>,
) {
  return createSelector([getLegacyApplications, getEntryPoint], (applications, entryPoint) => {
    const application = applications[entryPoint.applicationId];
    if (!application) {
      throw new Error('Application could not be found for entry point.');
    }

    return application;
  });
}

/*
 * Entry points.
 */

const getLegacyEntryPoints = createSelector(getFlatEnabledLegacyApplications, (applications) =>
  chain(applications)
    .flatMap((application) =>
      application.entryPoints.map((entryPoint): LegacyEntryPoint => {
        const uid = `${application.id}:${entryPoint.id}`;
        return {
          uid,
          type: entryPoint.type,
          applicationId: application.id,
          applicationTypeId: application.typeId,
          entryPointId: entryPoint.id,
          frameType: FrameTypesEnum.ENTRY_POINT,
          frameId: `entryPoint:${uid}`,
          name: entryPoint.name,
          color: application.color,
          iconUrl: application.iconUrl,
          iconBaseUrl: application.baseUrl && `${application.baseUrl}/public/icons/`,
        };
      }),
    )
    .sortBy((e) => e.name)
    .value(),
);

export const getLegacyConversationLinkDropdownEntryPoints = createSelector(
  getLegacyEntryPoints,
  (entryPoints) =>
    chain(entryPoints)
      .map((e) => e.type === EntryPointTypesEnum.CONVERSATION_LINK_DROPDOWN && e)
      .compact()
      .value(),
);

export const getLegacyMessageMoreDropdownEntryPoints = createSelector(getLegacyEntryPoints, (entryPoints) =>
  chain(entryPoints)
    .map((e) => e.type === EntryPointTypesEnum.MESSAGE_MORE_DROPDOWN && e)
    .compact()
    .value(),
);

export const getLegacySidePanelEntryPoints = createSelector(getLegacyEntryPoints, (entryPoints) =>
  chain(entryPoints)
    .map((e) => e.type === EntryPointTypesEnum.SIDE_PANEL && e)
    .compact()
    .value(),
);
